export type EnvironmentId = "production" | "staging" | "sandbox" | "preview" | "local";
export type DataEnvironmentId = Exclude<EnvironmentId, "preview"> | "unknown";
export type SwitchableEnvironmentId = Exclude<EnvironmentId, "local" | "preview">;

export type EnvironmentDescription = {
  appEnv: EnvironmentId;
  dataEnv: DataEnvironmentId;
  dataHost: string;
  label: string;
  showBadge: boolean;
  displayEnv: DataEnvironmentId;
};

const RAW_ENV_TO_APP_ENV: Record<string, EnvironmentId> = {
  production: "production",
  staging: "staging",
  development: "sandbox",
  preview: "preview",
  "aws-preview": "preview",
  local: "local",
};

const MAP_HOST_MARKERS: Array<[marker: string, env: Exclude<EnvironmentId, "preview">]> = [
  ["map-coordinator-prd", "production"],
  ["map-coordinator-stg", "staging"],
  ["map-coordinator-dev", "sandbox"],
];

const ENVIRONMENT_LABELS: Record<EnvironmentId | "unknown", string> = {
  production: "Production",
  staging: "Staging",
  sandbox: "Sandbox",
  preview: "Preview",
  local: "Local",
  unknown: "Unknown",
};

export const ENVIRONMENTS: Record<
  SwitchableEnvironmentId,
  { id: SwitchableEnvironmentId; label: string; url: string }
> = {
  production: {
    id: "production",
    label: "Production",
    url: "https://architect.machinalabs.app",
  },
  staging: {
    id: "staging",
    label: "Staging",
    url: "https://machina-app-architect-stg.azurewebsites.us",
  },
  sandbox: {
    id: "sandbox",
    label: "Sandbox",
    url: "https://sandbox-app-architect-dev.azurewebsites.us",
  },
};

export function environmentLabel(id: EnvironmentId | "unknown"): string {
  return ENVIRONMENT_LABELS[id];
}

export function resolveAppEnvironment(rawEnv: string | undefined): EnvironmentId {
  return RAW_ENV_TO_APP_ENV[rawEnv?.trim().toLowerCase() ?? ""] ?? "local";
}

export function resolveDataEnvironment(mapApiUrl: string | undefined): DataEnvironmentId {
  const url = mapApiUrl?.toLowerCase() ?? "";
  return MAP_HOST_MARKERS.find(([marker]) => url.includes(marker))?.[1] ?? "unknown";
}

function hostOf(mapApiUrl: string | undefined): string {
  const raw = mapApiUrl?.trim() ?? "";
  if (!raw) return "";
  try {
    return new URL(raw).host;
  } catch {
    return raw;
  }
}

export function describeEnvironment(
  rawEnv: string | undefined,
  mapApiUrl: string | undefined
): EnvironmentDescription {
  const appEnv = resolveAppEnvironment(rawEnv);
  const dataEnv = resolveDataEnvironment(mapApiUrl);
  const collapsed = dataEnv === appEnv;

  return {
    appEnv,
    dataEnv,
    dataHost: hostOf(mapApiUrl),
    label: collapsed
      ? ENVIRONMENT_LABELS[appEnv]
      : `${ENVIRONMENT_LABELS[appEnv]} · ${ENVIRONMENT_LABELS[dataEnv].toLowerCase()} data`,
    showBadge: appEnv !== "production" || dataEnv !== "production",
    displayEnv: dataEnv,
  };
}

export function environmentAppName(appEnv: EnvironmentId): string {
  return appEnv === "production" ? "Architect" : `${ENVIRONMENT_LABELS[appEnv]} Architect`;
}

const RAW_APP_ENV = process.env.NEXT_PUBLIC_DATADOG_ENV;
const RAW_MAP_API_URL = process.env.NEXT_PUBLIC_MAP_API_URL;

export function currentEnvironment(): EnvironmentDescription {
  return describeEnvironment(RAW_APP_ENV, RAW_MAP_API_URL);
}

/**
 * Whether the debug tooling is offered at all. Everywhere but real production: preview and
 * staging are production builds, so a NODE_ENV check would exclude the environments people
 * actually debug in.
 */
export function debugToolsEnabled(): boolean {
  return currentEnvironment().appEnv !== "production";
}

export function currentAppName(): string {
  return environmentAppName(resolveAppEnvironment(RAW_APP_ENV));
}
